import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldAlert, Check, X, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { betaApply } from "@/lib/api";

/**
 * Beta applications (admin), 2026-10-11. Applications from acars.flyvac.org/apply,
 * pending first; Approve adds the pilot to beta_testers, which VACARS5 and myVAC
 * both read, so they can sign in straight away. Below, everyone who is in and
 * why. Welcoming them on Discord (beta channel, download link) stays manual.
 */

const when = (s) => (s ? new Date(Number(s) * 1000).toISOString().replace("T", " ").slice(0, 16) + "Z" : "");
const nameOf = (r) => [r.first_name, r.last_name].filter(Boolean).join(" ");
const hours = (sec) => (sec ? `${Math.round(Number(sec) / 3600).toLocaleString()} h` : "");
const STATUS = {
  pending: "border-primary text-primary",
  approved: "border-emerald-500 text-emerald-600 dark:text-emerald-400",
  declined: "border-border text-muted-foreground",
};
const errText = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "That didn't work; try again.");

function Applications({ apps }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const act = async (id, fn, done) => {
    setBusy(id);
    try { await fn(); toast.success(done); qc.invalidateQueries({ queryKey: ["beta-applications"] }); qc.invalidateQueries({ queryKey: ["beta-testers"] }); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(null); }
  };
  const pending = apps.filter((a) => a.status === "pending");
  const shown = showAll ? apps : pending;

  return (
    <section className="mb-10" data-testid="beta-apps">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-lg font-bold">Applications <span className="text-muted-foreground font-normal">· {pending.length} waiting</span></h2>
        <button onClick={() => setShowAll((v) => !v)} className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-primary">
          {showAll ? "Waiting only" : `Show all (${apps.length})`}
        </button>
      </div>
      {shown.length === 0 ? (
        <div className="card-flat p-6 text-sm text-muted-foreground">{showAll ? "No applications yet." : "Nothing waiting."}</div>
      ) : (
        <div className="space-y-3">
          {shown.map((a) => (
            <div key={a.id} className="card-flat p-4" data-testid={`beta-app-${a.id}`}>
              <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold">{a.pid}</span>
                    <span>{nameOf(a)}</span>
                    <span className={`text-[10px] font-mono uppercase tracking-widest border rounded px-1.5 py-0.5 ${STATUS[a.status] || ""}`}>{a.status}</span>
                  </div>
                  <div className="text-sm mt-1">{a.setup_label} · {a.sim_version}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Discord <span className="font-mono">{a.discord}</span>
                    {a.hub ? ` · ${a.hub}` : ""}{a.hours ? ` · ${hours(a.hours)}` : ""} · applied {when(a.created_at)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Signed “{a.signed_name}” · confirmed 18 or older · agreement v{a.agreement_version}
                  </div>
                  {a.notes && <p className="text-sm mt-2 whitespace-pre-wrap border-l-2 border-border pl-3">{a.notes}</p>}
                  {a.status !== "pending" && (
                    <div className="text-xs text-muted-foreground mt-2">
                      {a.status} by {a.decided_by} {when(a.decided_at)}{a.decline_reason ? ` · “${a.decline_reason}”` : ""}
                    </div>
                  )}
                </div>
                {a.status === "pending" && (
                  <div className="flex gap-2">
                    <button disabled={busy === a.id} onClick={() => act(a.id, () => betaApply.approve(a.id), `${a.pid} approved — they can sign in now`)}
                      className="text-xs bg-primary text-primary-foreground px-3 py-2 rounded-md font-mono uppercase tracking-wider hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5"
                      data-testid={`beta-app-approve-${a.id}`}>
                      {busy === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approve
                    </button>
                    <button disabled={busy === a.id} onClick={() => {
                      const reason = window.prompt(`Decline ${a.pid}? Optional note for the team (not sent to the pilot):`, "");
                      if (reason !== null) act(a.id, () => betaApply.decline(a.id, reason), `${a.pid} declined`);
                    }}
                      className="text-xs border border-border px-3 py-2 rounded-md font-mono uppercase tracking-wider hover:border-primary disabled:opacity-50 inline-flex items-center gap-1.5">
                      <X className="w-3.5 h-3.5" /> Decline
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Testers({ data }) {
  const qc = useQueryClient();
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["beta-testers"] });
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await betaApply.addTester(pid); toast.success(`${r.pid} added`); setPid(""); refresh(); }
    catch (e2) { toast.error(errText(e2)); }
    finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p} from the beta? They'll be signed out of VACARS5 and myVAC.`)) return;
    try { await betaApply.removeTester(p); toast.success(`${p} removed`); refresh(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <section data-testid="beta-testers">
      <h2 className="text-lg font-bold mb-1">Testers <span className="text-muted-foreground font-normal">· {data.testers.length}</span></h2>
      <p className="text-sm text-muted-foreground mb-3">
        {data.closed
          ? "The beta is closed: only these pilots and admins can sign in to VACARS5 and myVAC."
          : data.file === null
            ? "The beta is open: there is no tester file on the server, so anyone can sign in."
            : "The tester file lets everyone in (*), so the beta is open."}
      </p>
      <form onSubmit={add} className="flex gap-2 mb-4 max-w-md">
        <input value={pid} onChange={(e) => setPid(e.target.value)} placeholder="Add by pilot ID, e.g. VAC017"
          className="field flex-1 px-3 py-2 text-sm font-mono" data-testid="beta-tester-add" />
        <button disabled={busy || !pid.trim()} className="text-xs border border-border px-3 py-2 rounded-md font-mono uppercase tracking-wider hover:border-primary disabled:opacity-50 inline-flex items-center gap-1.5">
          <UserPlus className="w-3.5 h-3.5" /> Add
        </button>
      </form>
      <div className="card-flat divide-y divide-border">
        {data.testers.length === 0 && <div className="p-4 text-sm text-muted-foreground">No testers added here yet.</div>}
        {data.testers.map((t) => (
          <div key={t.pid} className="p-3 flex items-center gap-3 text-sm">
            <span className="font-mono font-bold w-28">{t.pid}</span>
            <span className="flex-1">{nameOf(t)}</span>
            <span className="text-xs text-muted-foreground">
              {t.application_id ? `application #${t.application_id}` : "added by hand"} · {t.added_by || "?"} · {when(t.added_at)}
            </span>
            <button onClick={() => remove(t.pid)} title="Remove from the beta" className="p-1.5 text-muted-foreground hover:text-primary">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      {data.file && data.file.filter((p) => p !== "*").length > 0 && (
        <p className="text-xs text-muted-foreground mt-3">
          Also let in by the tester file on the server (edit /etc/vacars5/beta_pilots to change):{" "}
          <span className="font-mono">{data.file.filter((p) => p !== "*").join(", ")}</span>
        </p>
      )}
    </section>
  );
}

/** Open or close sign-up (2026-10-15): between betas the apply page says
 *  applications are closed, and nothing else is removed. */
function SignupSwitch() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["beta-settings"], queryFn: betaApply.settings });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  if (isLoading) return null;
  if (error) {
    return <div className="card-flat p-4 mb-6 text-sm text-primary">Couldn't read the sign-up switch. Is migration 012 applied on the server?</div>;
  }
  const open = !!data.applications_open;
  const message = msg ?? data.message ?? "";
  const save = async (nextOpen) => {
    setBusy(true);
    try {
      await betaApply.saveSettings(nextOpen, message);
      toast.success(nextOpen ? "Beta applications are open" : "Beta applications are closed");
      setMsg(null);
      qc.invalidateQueries({ queryKey: ["beta-settings"] });
      qc.invalidateQueries({ queryKey: ["beta-apply-open"] });
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <section className="card-flat p-4 mb-8" data-testid="beta-signup-switch">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`text-[10px] font-mono uppercase tracking-widest border rounded px-1.5 py-0.5 ${open ? STATUS.approved : STATUS.declined}`}>
          {open ? "Applications open" : "Applications closed"}
        </span>
        <span className="text-sm text-muted-foreground flex-1 min-w-[200px]">
          {open ? "Pilots can apply at acars.flyvac.org/apply." : "The apply page says applications are closed; testers and past applications are kept."}
        </span>
        <button disabled={busy} onClick={() => save(!open)} data-testid="beta-signup-toggle"
          className={`text-xs px-3 py-2 rounded-md font-mono uppercase tracking-wider disabled:opacity-50 ${open ? "border border-border hover:border-primary" : "bg-primary text-primary-foreground hover:brightness-110"}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : open ? "Close applications" : "Open applications"}
        </button>
      </div>
      <label className="block mt-3">
        <span className="overline block mb-1.5">Message shown while closed (optional)</span>
        <div className="flex gap-2">
          <input value={message} onChange={(e) => setMsg(e.target.value)} maxLength={500}
            placeholder="We're not taking new beta testers right now. Watch the FlyVAC Discord for the next beta."
            className="field flex-1 px-3 py-2 text-sm" data-testid="beta-signup-message" />
          <button disabled={busy || msg === null} onClick={() => save(open)}
            className="text-xs border border-border px-3 py-2 rounded-md font-mono uppercase tracking-wider hover:border-primary disabled:opacity-50">Save</button>
        </div>
      </label>
    </section>
  );
}

export default function BetaApplications() {
  const { user } = useAuth();
  const apps = useQuery({ queryKey: ["beta-applications"], queryFn: betaApply.applications, enabled: !!user?.is_admin, refetchInterval: 60_000 });
  const testers = useQuery({ queryKey: ["beta-testers"], queryFn: betaApply.testers, enabled: !!user?.is_admin });

  if (!user?.is_admin) {
    return (
      <div className="p-8"><div className="card-flat p-10 text-center text-sm text-muted-foreground">
        <ShieldAlert className="w-6 h-6 mx-auto mb-2" />Beta applications are for admins.
      </div></div>
    );
  }

  const failed = apps.error || testers.error;
  return (
    <div className="p-8 max-w-5xl" data-testid="beta-applications-page">
      <div className="mb-6">
        <div className="overline mb-1">Admin</div>
        <h1 className="text-3xl font-bold tracking-tight">Beta applications</h1>
        <div className="acbar" />
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
          Pilots apply at <span className="font-mono">acars.flyvac.org/apply</span>. Approving lets them sign in to
          VACARS5 and myVAC straight away; then welcome them on Discord with the beta channel and the download link.
        </p>
      </div>
      <SignupSwitch />
      {apps.isLoading || testers.isLoading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : failed ? (
        <div className="card-flat p-6 text-sm text-primary">
          Couldn't load applications. If this is the first time, migration 011 may not be applied on the server yet.
        </div>
      ) : (
        <>
          <Applications apps={apps.data || []} />
          <Testers data={testers.data} />
        </>
      )}
    </div>
  );
}
